import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, Trash, Save } from "lucide-react";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

export default function DriverConfigModal({
  open,
  onOpenChange,
  fetchSingleDriverConfig,
  saveSingleDriverConfig,
  deleteSingleDriverConfig,
  driverCode, // PD0000xx
  onSuccess, // refresh callback
}) {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [existingConfig, setExistingConfig] = useState(null);

  const { AdminId } = useGlobalContext();

  const [form, setForm] = useState({
    cashLimit: "",
    dailyTaskLimit: "",
    concurrentOrder: "",
    maxTeamLimit: "",
  });

  const title = useMemo(() => {
    return existingConfig
      ? `Update Configuration (${driverCode})`
      : `Add Configuration (${driverCode})`;
  }, [existingConfig, driverCode]);

  const resetLocal = () => {
    setExistingConfig(null);
    setForm({
      cashLimit: "",
      dailyTaskLimit: "",
      concurrentOrder: "",
      maxTeamLimit: "",
    });
  };

  const fetchExisting = async () => {
    await fetchSingleDriverConfig(
      driverCode,
      setFetching,
      setExistingConfig,
      setForm,
    );
  };

  useEffect(() => {
    if (open) fetchExisting();
    else resetLocal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, driverCode]);

  const onChange = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const validate = () => {
    const numericKeys = [
      "cashLimit",
      "dailyTaskLimit",
      "concurrentOrder",
      "maxTeamLimit",
    ];
    for (const k of numericKeys) {
      const v = (form[k] ?? "").toString().trim();
      if (!v) return `${k} is required`;
      if (!/^\d+(\.\d+)?$/.test(v)) return `${k} must be numeric`;
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) return toast.error(err);

    setLoading(true);

    const payload = {
      adminId: AdminId, // still coming from parent/context if needed
      driverCode,
      cashLimit: form.cashLimit.toString(),
      dailyTaskLimit: form.dailyTaskLimit.toString(),
      concurrentOrder: form.concurrentOrder.toString(),
      maxTeamLimit: form.maxTeamLimit.toString(),
    };

    const success = await saveSingleDriverConfig(payload, existingConfig);

    setLoading(false);

    if (success) {
      onSuccess?.();
      onOpenChange(false);
    }
  };

  const handleDelete = async () => {
    if (!existingConfig?.pk_config_id)
      return toast.error("No configuration to delete");

    const ok = window.confirm(`Delete configuration for ${driverCode}?`);
    if (!ok) return;

    setLoading(true);

    const success = await deleteSingleDriverConfig(existingConfig.pk_config_id);

    setLoading(false);

    if (success) {
      onSuccess?.();
      onOpenChange(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-50" />
        <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92%] max-w-2xl bg-white rounded-xl shadow-lg">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <div>
              <Dialog.Title className="text-base font-semibold text-gray-900">
                {title}
              </Dialog.Title>
              <p className="text-xs text-gray-500 mt-0.5">
                {fetching
                  ? "Loading existing configuration..."
                  : "Set limits for this driver"}
              </p>
            </div>
            <Dialog.Close asChild>
              <button className="p-2 rounded hover:bg-gray-100">
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <div className="px-5 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field
                label="Cash Limit"
                value={form.cashLimit}
                onChange={(v) => onChange("cashLimit", v)}
                placeholder="e.g. 2000"
              />
              <Field
                label="Daily Task Limit"
                value={form.dailyTaskLimit}
                onChange={(v) => onChange("dailyTaskLimit", v)}
                placeholder="e.g. 15"
              />
              <Field
                label="Concurrent Order"
                value={form.concurrentOrder}
                onChange={(v) => onChange("concurrentOrder", v)}
                placeholder="e.g. 3"
              />
              <Field
                label="Max Team Limit"
                value={form.maxTeamLimit}
                onChange={(v) => onChange("maxTeamLimit", v)}
                placeholder="e.g. 3"
              />
            </div>

            {existingConfig && (
              <div className="mt-4 rounded-lg border  p-3">
                <div className="text-xs text-gray-600 flex flex-wrap gap-4">
                  <div>
                    <span className="font-semibold text-gray-700">
                      Config ID:
                    </span>{" "}
                    {existingConfig.pk_config_id}
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">
                      Driver Type:
                    </span>{" "}
                    {existingConfig.driver_type || "-"}
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">
                      Team ID:
                    </span>{" "}
                    {existingConfig.team_id ?? "-"}
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">
                      Created:
                    </span>{" "}
                    {existingConfig.created_on
                      ? String(existingConfig.created_on)
                      : "-"}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="px-5 py-4 border-t flex items-center justify-between gap-2">
            <button
              className="inline-flex items-center gap-2 px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm disabled:opacity-50"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Close
            </button>

            <div className="flex items-center gap-2">
              <button
                className="inline-flex items-center gap-2 px-3 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm disabled:opacity-50"
                onClick={handleDelete}
                disabled={loading || !existingConfig}
                title={
                  !existingConfig
                    ? "No existing config to delete"
                    : "Delete config"
                }
              >
                <Trash size={16} />
                Delete
              </button>

              <button
                className="inline-flex items-center gap-2 px-3 py-2 rounded bg-orange-600 hover:bg-orange-700 text-white text-sm disabled:opacity-50"
                onClick={handleSave}
                disabled={loading}
              >
                <Save size={16} />
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div className="flex flex-col">
      <label className="text-xs font-semibold text-gray-700 mb-1">
        {label}
      </label>
      <input
        className="border rounded px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
      />
    </div>
  );
}
